import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserData } from "lib/store/user";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { updateZephrUserProfile } from "lib/fetchers/zephr/customFields";
import {
  COMPLETE_PROFILE_DIALOG_ID,
  COMPLETE_PROFILE_SUBMITTED_MARKER,
} from "lib/zephr/constants";
import {
  buildCompleteProfileUrl,
  markPromptSkippedForCurrentLogin,
} from "lib/zephr/complete-profile-prompt";
import {
  applyFieldVisibility,
  hasAnyAnswer,
  readFieldValues,
  type FieldElements,
} from "lib/zephr/complete-profile-fields";
import { Section, useZephrForm } from "../useZephrForm";

export const DIALOG_ID = COMPLETE_PROFILE_DIALOG_ID;
export const DIALOG_FORM_SELECTOR = `#${DIALOG_ID} form`;

const FORM_WAIT_TIMEOUT_MS = 4000;
const FORM_POLL_MS = 100;

type FieldValues = { [key: string]: string | boolean };

const waitForForm = (): Promise<HTMLFormElement | null> =>
  new Promise((resolve) => {
    const startedAt = Date.now();

    const intervalId = window.setInterval(() => {
      const form = document.querySelector<HTMLFormElement>(
        DIALOG_FORM_SELECTOR
      );

      if (form) {
        window.clearInterval(intervalId);
        resolve(form);
        return;
      }

      if (Date.now() - startedAt >= FORM_WAIT_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        resolve(null);
      }
    }, FORM_POLL_MS);
  });

const ModalFormBehaviour = ({
  profileValues,
  onValues,
}: {
  profileValues: FieldValues;
  onValues: (values: FieldValues) => void;
}) => {
  const initialValues = useRef<FieldValues>({});

  const onLoad = useCallback(
    (fields: FieldElements) => {
      initialValues.current = readFieldValues(fields);
      applyFieldVisibility(profileValues, fields);
    },
    [profileValues]
  );

  const onUpdate = useCallback(
    (_section: string, fields: FieldElements) => {
      applyFieldVisibility(profileValues, fields);
      onValues(readFieldValues(fields));
    },
    [profileValues, onValues]
  );

  const hasChanges = useCallback(
    (fields: FieldElements) =>
      Object.entries(readFieldValues(fields)).some(
        ([name, value]) => value !== initialValues.current[name]
      ),
    []
  );

  useZephrForm({
    selector: DIALOG_FORM_SELECTOR,
    section: Section.COMPLETE_PROFILE_OPTIONAL,
    onLoad,
    onUpdate,
    canSubmit: hasChanges,
  });

  return null;
};

interface CompleteProfileModalProps {
  skipRef: MutableRefObject<() => void>;
  onReady: () => void;
  onClose: () => void;
}

export const CompleteProfileModal = ({
  skipRef,
  onReady,
  onClose,
}: CompleteProfileModalProps) => {
  const { userProfile } = useUserData();
  const { track } = useMixpanelTracking();
  const [formReady, setFormReady] = useState(false);
  const latestValues = useRef<FieldValues>({});
  const finishedRef = useRef(false);

  const profileValues = useMemo(
    () => ({
      ...userProfile.details,
      first_name: userProfile.first_name,
      last_name: userProfile.last_name,
    }),
    [userProfile]
  );

  const finish = useCallback(async () => {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;

    const answered = hasAnyAnswer(latestValues.current);

    try {
      await track("Complete profile submitted", {
        Component: "CompleteProfileModal",
        Answered: answered,
      });

      if (answered) {
        await updateZephrUserProfile({
          [COMPLETE_PROFILE_SUBMITTED_MARKER]: true,
        });
      } else {
        markPromptSkippedForCurrentLogin();
      }
    } finally {
      onClose();
    }
  }, [onClose, track]);

  const skip = useCallback(async () => {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;

    try {
      await track("Complete profile skipped", {
        Component: "CompleteProfileModal",
      });

      markPromptSkippedForCurrentLogin();
    } finally {
      onClose();
    }
  }, [onClose, track]);

  const finishRef = useRef(finish);

  useEffect(() => {
    skipRef.current = skip;
    finishRef.current = finish;
  }, [skipRef, skip, finish]);

  useEffect(() => {
    let cancelled = false;

    const open = async () => {
      const form = await waitForForm();

      if (cancelled) {
        return;
      }

      if (!form) {
        window.location.assign(
          buildCompleteProfileUrl(
            window.location.pathname + window.location.search
          )
        );
        return;
      }

      setFormReady(true);
      onReadyRef.current();
    };

    open();

    return () => {
      cancelled = true;
    };
  }, []);

  const onReadyRef = useRef(onReady);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    if (!formReady) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        skipRef.current();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [formReady, skipRef]);

  useEffect(() => {
    if (!formReady) {
      return;
    }

    const container = document.getElementById(DIALOG_ID);

    if (!container) {
      return;
    }

    const update = () => {
      const feature = container.querySelector(".zephr-feature");

      if (!feature) {
        return;
      }

      if (
        (feature.textContent ?? "").trim() !== "" &&
        !feature.querySelector("input, select")
      ) {
        finishRef.current();
      }
    };

    const observer = new MutationObserver(update);
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [formReady]);

  if (!formReady) {
    return null;
  }

  return (
    <ModalFormBehaviour
      profileValues={profileValues}
      onValues={(values) => {
        latestValues.current = values;
      }}
    />
  );
};

export default CompleteProfileModal;
